/**
 * Static-page video config for MuxVideoPlayerWithCTA.
 * channel_invite (grupo-privado) - principal.
 */

import {
  TELEGRAM_GROUPS_PLAYBACK_ID,
  TELEGRAM_GROUPS_CTA_URL,
} from "@/config/telegramGroupsVideo";

export interface StaticPageVideoConfig {
  playbackId: string;
  ctaUrl: string;
  ctaLabel: string;
  title?: string;
  showInstagramIcon?: boolean;
}

export interface StaticPageVideoEntry {
  page: string;
  ctaEnvKey: string;
  config: StaticPageVideoConfig;
}

export const STATIC_PAGE_VIDEO_REGISTRY: StaticPageVideoEntry[] = [
  {
    page: "grupo-privado",
    ctaEnvKey: "VITE_TELEGRAM_GROUPS_CTA_URL",
    config: {
      playbackId: TELEGRAM_GROUPS_PLAYBACK_ID,
      ctaUrl: TELEGRAM_GROUPS_CTA_URL,
      ctaLabel: "Unirte",
      title: "Telegram (acceso al grupo, no VIP)",
      showInstagramIcon: false,
    },
  },
];

export type StaticPageVideoPage = (typeof STATIC_PAGE_VIDEO_REGISTRY)[number]["page"];

/** Pathnames that map to static-page video config. */
export const STATIC_PAGE_VIDEO_PATHS: StaticPageVideoPage[] =
  STATIC_PAGE_VIDEO_REGISTRY.map((e) => e.page as StaticPageVideoPage);

/** Env var key for CTA URL per static page. */
export const STATIC_PAGE_CTA_ENV_KEYS: Record<StaticPageVideoPage, string> =
  Object.fromEntries(
    STATIC_PAGE_VIDEO_REGISTRY.map((e) => [e.page, e.ctaEnvKey])
  ) as Record<StaticPageVideoPage, string>;

const CONFIG_MAP: Record<StaticPageVideoPage, StaticPageVideoConfig> =
  Object.fromEntries(
    STATIC_PAGE_VIDEO_REGISTRY.map((e) => [e.page, e.config])
  ) as Record<StaticPageVideoPage, StaticPageVideoConfig>;

/**
 * Get video + CTA config for a static page.
 * Used by MuxVideoPlayerWithCTA when `page` is set or pathname matches.
 */
export function getStaticPageVideoConfig(
  page: StaticPageVideoPage
): StaticPageVideoConfig {
  const c = CONFIG_MAP[page];
  return c ? { ...c } : { playbackId: "", ctaUrl: "", ctaLabel: "Unirte" };
}

/**
 * Resolve static page from pathname (e.g. /grupo-privado → "grupo-privado").
 * Returns null if pathname is not a known static-page video route.
 */
export function getStaticPageFromPathname(
  pathname: string
): StaticPageVideoPage | null {
  const normalized = pathname.replace(/^\/+|\/+$/g, "") || "";
  if (STATIC_PAGE_VIDEO_PATHS.includes(normalized as StaticPageVideoPage)) {
    return normalized as StaticPageVideoPage;
  }
  return null;
}

/**
 * Path for a static page (e.g. "grupo-privado" → "/grupo-privado").
 * Use for canonical URLs, tracking page_path, etc.
 */
export function getPagePathForStaticPage(page: StaticPageVideoPage): string {
  return `/${page}`;
}
